import { useCallback, useMemo, useState, type FormEvent } from 'react';
import type { StatusPage, StatusPageComponent } from '@detect/shared';
import { statusPageService, type DomainSetup } from '../../services/status-pages';
import { clientService } from '../../services/clients';
import { resourceService } from '../../services/resources';
import type { WithId } from '../../services/types';
import { useTenant } from '../../contexts/TenantContext';
import { useCursorList } from '../../hooks/useCursorList';
import { usePickerOptions } from '../../hooks/usePickerOptions';
import {
  LoadingSpinner,
  ErrorState,
  EmptyState,
  Modal,
  LoadMoreButton,
} from '../../components/common';

/** White-label public status pages (PRD §2.7 — the Guru's
 *  proof-of-value). Admin-only mutations; slugs are platform-global. */
export function StatusPagesPage() {
  const { tenantId } = useTenant();
  const [editing, setEditing] = useState<WithId<StatusPage> | 'new' | null>(null);
  const [domainSetup, setDomainSetup] = useState<DomainSetup | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);

  const fetchPage = useCallback(
    (tid: string, startAfter: string | undefined) =>
      statusPageService.list(tid, { limit: 50, startAfter }),
    []
  );
  const list = useCursorList<WithId<StatusPage>>(fetchPage, 'all');

  const remove = async (page: WithId<StatusPage>) => {
    if (!tenantId) return;
    if (
      !window.confirm(
        `Delete status page ${page.name}? Its public URL stops working immediately.`
      )
    ) {
      return;
    }
    setActionError(null);
    try {
      await statusPageService.remove(tenantId, page.id);
      void list.reload();
    } catch (err) {
      setActionError(err);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Status pages</h1>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setEditing('new')}
        >
          New status page
        </button>
      </div>
      {actionError !== null && <ErrorState error={actionError} />}

      {list.error ? (
        <ErrorState error={list.error} onRetry={() => void list.reload()} />
      ) : list.loading ? (
        <LoadingSpinner />
      ) : list.items.length === 0 ? (
        <EmptyState
          title="No status pages"
          description="Publish a branded uptime page for a client — their proof of your value."
        />
      ) : (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th>Custom domain</th>
                <th>Components</th>
                <th>Enabled</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {list.items.map((p) => (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td>
                    <code>{p.slug}</code>
                  </td>
                  <td>
                    {p.customDomain ? (
                      <>
                        {p.customDomain.domain}{' '}
                        <span
                          className={`status-badge status-${
                            p.customDomain.status === 'active'
                              ? 'active'
                              : p.customDomain.status === 'failed'
                                ? 'down'
                                : 'pending'
                          }`}
                          title={p.customDomain.failureReason ?? undefined}
                        >
                          {p.customDomain.status}
                        </span>
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{p.components.length}</td>
                  <td>{p.enabled ? 'yes' : 'no'}</td>
                  <td className="row-actions">
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => setEditing(p)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => void remove(p)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <LoadMoreButton
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        loadMoreError={list.loadMoreError}
        onLoadMore={() => void list.loadMore()}
      />

      {editing !== null && (
        <StatusPageModal
          page={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(setup) => {
            setEditing(null);
            setDomainSetup(setup ?? null);
            void list.reload();
          }}
        />
      )}
      {domainSetup && domainSetup.requiredRecords.length > 0 && (
        <Modal title="DNS records required" onClose={() => setDomainSetup(null)}>
          <p>
            Add these DNS records at the domain registrar; verification runs
            automatically (check back on this page for status).
          </p>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Name</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {domainSetup.requiredRecords.map((r, i) => (
                  <tr key={i}>
                    <td>{r.type}</td>
                    <td>
                      <code>{r.name}</code>
                    </td>
                    <td>
                      <code>{r.value}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}
    </div>
  );
}

interface ComponentDraft {
  resourceId: string;
  displayName: string;
}

function StatusPageModal({
  page,
  onClose,
  onSaved,
}: {
  page: WithId<StatusPage> | null;
  onClose: () => void;
  onSaved: (setup?: DomainSetup) => void;
}) {
  const { tenantId, clientScope } = useTenant();
  const [name, setName] = useState(page?.name ?? '');
  const [slug, setSlug] = useState(page?.slug ?? '');
  const [clientId, setClientId] = useState(page?.clientId ?? '');
  const [enabled, setEnabled] = useState(page?.enabled ?? true);
  const [customDomain, setCustomDomain] = useState(page?.customDomain?.domain ?? '');
  const [components, setComponents] = useState<ComponentDraft[]>(
    page
      ? [...page.components]
          .sort((a, b) => a.order - b.order)
          .map((c) => ({ resourceId: c.resourceId, displayName: c.displayName }))
      : []
  );
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  const clientsQuery = usePickerOptions(['clients'], (tid, startAfter) =>
    clientService.list(tid, { limit: 100, startAfter })
  );
  const clients = (clientsQuery.data?.items ?? []).filter(
    (c) => clientScope === null || clientScope.includes(c.id)
  );
  const resourcesQuery = usePickerOptions(['resources'], (tid, startAfter) =>
    resourceService.list(tid, { limit: 100, startAfter })
  );
  // Components must belong to the page's client (API-enforced).
  const clientResources = useMemo(
    () =>
      (resourcesQuery.data?.items ?? []).filter(
        (r) => clientId !== '' && r.clientId === clientId
      ),
    [resourcesQuery.data, clientId]
  );

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;
    if (components.length === 0) {
      setError(new Error('Add at least one component'));
      return;
    }
    setSubmitting(true);
    setError(null);
    const componentPayload: StatusPageComponent[] = components.map((c, index) => {
      const resource = clientResources.find((r) => r.id === c.resourceId);
      return {
        kind: resource?.kind === 'website' ? 'website' : 'resource',
        resourceId: c.resourceId,
        displayName: c.displayName || (resource?.displayName ?? resource?.name ?? c.resourceId),
        order: index,
      };
    });
    try {
      if (page) {
        const result = await statusPageService.update(tenantId, page.id, {
          name,
          slug,
          clientId,
          enabled,
          components: componentPayload,
          customDomain: customDomain || null,
        });
        onSaved(result.domainSetup);
      } else {
        const result = await statusPageService.create(tenantId, {
          name,
          slug,
          clientId,
          enabled,
          components: componentPayload,
          ...(customDomain ? { customDomain } : {}),
        });
        onSaved(result.domainSetup);
      }
    } catch (err) {
      setError(err);
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={page ? `Edit ${page.name}` : 'New status page'}
      onClose={onClose}
      wide
    >
      <form onSubmit={submit}>
        {error !== null && <ErrorState error={error} />}
        <div className="form-field">
          <label htmlFor="sp-name">Name</label>
          <input
            id="sp-name"
            name="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="sp-slug">Slug (public subdomain; globally unique)</label>
          <input
            id="sp-slug"
            name="slug"
            required
            pattern="[a-z0-9]([a-z0-9-]*[a-z0-9])?"
            placeholder="acme-corp"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="sp-client">Client</label>
          <select
            id="sp-client"
            name="clientId"
            required
            value={clientId}
            onChange={(e) => {
              setClientId(e.target.value);
              setComponents([]); // components are per-client
            }}
          >
            <option value="">— select —</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="sp-domain">Custom domain (optional; DNS wizard follows)</label>
          <input
            id="sp-domain"
            name="customDomain"
            placeholder="status.acme.com"
            value={customDomain}
            onChange={(e) => setCustomDomain(e.target.value)}
          />
        </div>

        <h3>Components (ordered)</h3>
        {components.map((c, index) => (
          <div key={index} className="component-row">
            <label htmlFor={`comp-${index}-resource`} className="visually-hidden">
              Component {index + 1} resource
            </label>
            <select
              id={`comp-${index}-resource`}
              name={`comp-${index}-resource`}
              required
              value={c.resourceId}
              onChange={(e) =>
                setComponents((prev) =>
                  prev.map((x, i) =>
                    i === index ? { ...x, resourceId: e.target.value } : x
                  )
                )
              }
            >
              <option value="">— resource —</option>
              {clientResources.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.displayName ?? r.name}
                </option>
              ))}
            </select>
            <label htmlFor={`comp-${index}-name`} className="visually-hidden">
              Component {index + 1} display name
            </label>
            <input
              id={`comp-${index}-name`}
              name={`comp-${index}-name`}
              placeholder="Public display name"
              value={c.displayName}
              onChange={(e) =>
                setComponents((prev) =>
                  prev.map((x, i) =>
                    i === index ? { ...x, displayName: e.target.value } : x
                  )
                )
              }
            />
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() =>
                setComponents((prev) => prev.filter((_, i) => i !== index))
              }
            >
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn btn-secondary"
          disabled={clientId === ''}
          onClick={() =>
            setComponents((prev) => [...prev, { resourceId: '', displayName: '' }])
          }
        >
          Add component
        </button>
        {resourcesQuery.data?.truncated && (
          <p className="text-muted">Resource list truncated.</p>
        )}

        <div className="form-field form-field-inline">
          <input
            id="sp-enabled"
            name="enabled"
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <label htmlFor="sp-enabled">Enabled (publicly reachable)</label>
        </div>
        <div className="modal-footer">
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Saving…' : page ? 'Save' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
